import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, MessageSquare, Send, X, Menu, Trash2 } from 'lucide-react';
import axios from 'axios';

const BASE_URL = 'https://dr-mohamed-khaled.site';
const DOCTOR_ID = '5b075375-0b21-4e67-91c8-1ab2c709fa85';

export default function ChatPanel() {
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [searchChat, setSearchChat] = useState('');
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const selectedChat = chats.find(c => c.id === selectedChatId);
  const currentMessages = messages[selectedChatId] || [];

  const filteredChats = chats.filter(c =>
    c.name.toLowerCase().includes(searchChat.toLowerCase()) ||
    (c.lastMessage || '').toLowerCase().includes(searchChat.toLowerCase())
  );

  // ── Scroll to bottom ─────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentMessages]);

  // ── Normalize raw room from API into chat item ───────────────────
  const normalizeRoom = (room) => {
    // Handle last_message which can be either string or object
    let lastMessageText = '';
    if (room.last_message) {
      if (typeof room.last_message === 'string') {
        lastMessageText = room.last_message;
      } else if (room.last_message?.message) {
        lastMessageText = room.last_message.message;
      }
    } else if (room.lastMessage) {
      if (typeof room.lastMessage === 'string') {
        lastMessageText = room.lastMessage;
      } else if (room.lastMessage?.message) {
        lastMessageText = room.lastMessage.message;
      }
    }
    
    return {
      id: room.id ?? room._id,
      name: room.patient_name || 'مريض',
      phone: room.patient_phone || '',
      avatar: (room.patient_name || 'م')[0],
      online: room.status === 'active',
      status: room.status,
      lastMessage: lastMessageText,
      time: room.updated_at || room.updatedAt
        ? new Date(room.updated_at || room.updatedAt).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })
        : '',
      unread: room.unread_count || 0,
    };
  };

  // ── Normalize raw message from API ───────────────────────────────
  // Enhanced to handle various API response formats
  const normalizeMsg = (m) => {
    // Handle if m is not an object
    if (!m || typeof m !== 'object') {
      console.warn('Invalid message object:', m);
      return null;
    }

    return {
      id: m.id ?? m._id ?? String(Math.random()),
      sender: (m.sender_type ?? m.senderType) === 'doctor' ? 'admin' : 'user',
      text: m.message ?? m.text ?? '',
      time: m.created_at || m.createdAt
        ? new Date(m.created_at || m.createdAt).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })
        : '',
    };
  };

  // ── Fetch all chat rooms ─────────────────────────────────────────
  const fetchChats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BASE_URL}/api/chat/rooms`);
      const raw = data?.rooms ?? data?.data ?? (Array.isArray(data) ? data : []);
      const normalized = raw.map(normalizeRoom);
      setChats(normalized);
      // Auto-select first chat if none selected
      setSelectedChatId(prev => prev ?? normalized[0]?.id ?? null);
    } catch (err) {
      console.error('Failed to fetch chats', err);
    } finally {
      setLoadingChats(false);
    }
  }, []);

  // ── Fetch messages for a specific room ───────────────────────────
  const fetchMessages = useCallback(async (roomId, silent = false) => {
    if (!roomId) return;
    if (!silent) setLoadingMessages(true);
    try {
      const { data } = await axios.get(`${BASE_URL}/api/chat/rooms/${roomId}`);
      
      // Extract messages from data.room.messages (based on actual API response)
      const raw = data?.room?.messages || [];
      
      if (Array.isArray(raw) && raw.length > 0) {
        const normalized = raw
          .map(msg => normalizeMsg(msg))
          .filter(msg => msg !== null);
        setMessages(prev => ({ ...prev, [roomId]: normalized }));
      } else {
        setMessages(prev => ({ ...prev, [roomId]: [] }));
      }
    } catch (err) {
      console.error('Failed to fetch messages', err);
      setMessages(prev => ({ ...prev, [roomId]: [] }));
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, []);

  // ── Initial load ─────────────────────────────────────────────────
  useEffect(() => {
    fetchChats();
    // Poll rooms list every 10s for new conversations
    const roomsPoll = setInterval(fetchChats, 10000);
    return () => clearInterval(roomsPoll);
  }, [fetchChats]);

  // ── Poll messages for selected chat every 4s ─────────────────────
  useEffect(() => {
    clearInterval(pollRef.current);
    if (selectedChatId) {
      fetchMessages(selectedChatId);
      pollRef.current = setInterval(() => fetchMessages(selectedChatId, true), 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [selectedChatId, fetchMessages]);

  // ── Select chat ──────────────────────────────────────────────────
  const handleSelectChat = (chatId) => {
    setSelectedChatId(chatId);
    setChats(prev => prev.map(c => c.id === chatId ? { ...c, unread: 0 } : c));
    setSidebarOpen(false); // إغلاق الشريط بعد الاختيار
  };

  // ── Delete chat room ─────────────────────────────────────────────
  const handleDeleteChat = async (chatId, event) => {
    event?.stopPropagation();
    if (!chatId) return;

    const chatToDelete = chats.find(c => c.id === chatId);
    const confirmDelete = window.confirm(`هل تريد حذف محادثة ${chatToDelete?.name || 'هذا المريض'}؟`);
    if (!confirmDelete) return;

    try {
      await axios.delete(`${BASE_URL}/api/chat/rooms/${chatId}`);

      const remainingChats = chats.filter(c => c.id !== chatId);
      setChats(remainingChats);
      setMessages(prev => {
        const updated = { ...prev };
        delete updated[chatId];
        return updated;
      });

      if (selectedChatId === chatId) {
        setSelectedChatId(remainingChats[0]?.id ?? null);
      }
    } catch (err) {
      console.error('Failed to delete chat', err);
      window.alert('فشل حذف المحادثة');
    }
  };

  // ── Send message as doctor ───────────────────────────────────────
  const handleSend = async () => {
    const text = newMessage.trim();
    if (!text || !selectedChatId || sending) return;

    setNewMessage('');
    setSending(true);
    try {
      await axios.post(`${BASE_URL}/api/chat/messages`, {
        roomId: selectedChatId,
        senderType: 'doctor',
        senderId: DOCTOR_ID,
        message: text,
      });
      // Fetch fresh messages after send
      await fetchMessages(selectedChatId, true);
      // Update sidebar last message
      setChats(prev => prev.map(c =>
        c.id === selectedChatId
          ? { ...c, lastMessage: text, time: 'الآن' }
          : c
      ));
    } catch (err) {
      console.error('Failed to send message', err);
      // Restore message on failure
      setNewMessage(text);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="chat-section">
      {/* Overlay to close sidebar on mobile */}
      {sidebarOpen && (
        <div 
          className="chat-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <div className="chat-layout">

        {/* ── Sidebar ── */}
        <div className={`chat-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="chat-search-wrapper">
            <div className="chat-search">
              <Search size={16} />
              <input
                placeholder="بحث في المحادثات..."
                value={searchChat}
                onChange={(e) => setSearchChat(e.target.value)}
              />
              {searchChat && (
                <button className="clear-search" onClick={() => setSearchChat('')}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="chat-list">
            {loadingChats ? (
              <div className="chat-empty"><p>جارٍ التحميل...</p></div>
            ) : filteredChats.length === 0 ? (
              <div className="chat-empty"><Search size={24} /><p>لا توجد محادثات</p></div>
            ) : (
              filteredChats.map(chat => (
                <div
                  key={chat.id}
                  className={`chat-item ${selectedChatId === chat.id ? 'active' : ''}`}
                  onClick={() => handleSelectChat(chat.id)}
                >
                  <div className="chat-avatar-wrapper">
                    <div className="chat-avatar">{chat.avatar}</div>
                    {chat.online && <span className="online-indicator"></span>}
                  </div>
                  <div className="chat-info">
                    <div className="chat-name-row">
                      <span className="chat-name">{chat.name}</span>
                      <span className="chat-time">{chat.time}</span>
                    </div>
                    <div className="chat-name-row">
                      <span className="chat-last">{chat.lastMessage}</span>
                      {chat.unread > 0 && <span className="chat-unread">{chat.unread}</span>}
                    </div>
                  </div>
                  <button
                    className="chat-delete-btn"
                    onClick={(event) => handleDeleteChat(chat.id, event)}
                    aria-label={`حذف محادثة ${chat.name}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Main Chat ── */}
        <div className="chat-main">
          {selectedChat ? (
            <>
              <div className="chat-header">
                <button 
                  className="menu-toggle-chat" 
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  aria-label="فتح قائمة الشاتات"
                >
                  <Menu size={20} />
                </button>
                <div className="chat-header-info">
                  <div className="chat-avatar-wrapper">
                    <div className="chat-avatar large">{selectedChat.avatar}</div>
                    {selectedChat.online && <span className="online-indicator large"></span>}
                  </div>
                  <div className="chat-header-text">
                    <div className="chat-header-name">{selectedChat.name}</div>
                    <div className="chat-header-status">
                      {selectedChat.online
                        ? <><span className="online-dot"></span>متصل الآن</>
                        : <><span className="offline-dot"></span>غير متصل</>}
                    </div>
                    {selectedChat.phone && (
                      <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '2px' }}>
                        {selectedChat.phone}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="chat-messages">
                {loadingMessages ? (
                  <div className="messages-empty"><p>جارٍ تحميل الرسائل...</p></div>
                ) : currentMessages.length === 0 ? (
                  <div className="messages-empty">
                    <MessageSquare size={40} />
                    <p>ابدأ محادثة مع {selectedChat.name}</p>
                  </div>
                ) : (
                  currentMessages.map(msg => (
                    <div key={msg.id} className={`message ${msg.sender === 'admin' ? 'user' : 'admin'}`}>
                      <div className="message-bubble">
                        <p>{msg.text}</p>
                        <span className="message-time">{msg.time}</span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="chat-input-area">
                <div className="chat-input-wrapper">
                  <input
                    type="text"
                    placeholder="اكتب رسالتك هنا ..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    style={{ padding: '10px' }}
                    disabled={sending}
                  />
                  <button
                    className="send-btn"
                    onClick={handleSend}
                    disabled={!newMessage.trim() || sending}
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="chat-empty-main">
              <MessageSquare size={48} />
              <p>اختر محادثة</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}